import mapboxgl from 'mapbox-gl';

// 计算两点之间的距离（单位：千米）
export const calculateDistance = (coord1, coord2) => {
  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;
  
  const R = 6371; // 地球半径，单位千米
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance;
};

// 计算路线总距离
export const calculateTotalDistance = (coordinates) => {
  if (!coordinates || coordinates.length < 2) return 0;
  
  let totalDistance = 0;
  for (let i = 1; i < coordinates.length; i++) {
    totalDistance += calculateDistance(coordinates[i-1], coordinates[i]);
  }
  
  return totalDistance;
};

// 对路线坐标点进行按距离均匀采样，确保每隔samplingDistance米有一个点
export const sampleCoordinatesByDistance = (coordinates, samplingDistance = 100) => {
  if (coordinates.length < 2) return coordinates;
  
  const result = [];
  // 添加起点
  result.push(coordinates[0]);
  
  let accumulatedDistance = 0;
  let lastSampledPoint = coordinates[0];
  
  for (let i = 1; i < coordinates.length; i++) {
    const currentPoint = coordinates[i];
    const segmentDistance = calculateDistance(lastSampledPoint, currentPoint) * 1000; // 转为米
    
    accumulatedDistance += segmentDistance;
    
    if (accumulatedDistance >= samplingDistance) {
      result.push(currentPoint);
      lastSampledPoint = currentPoint;
      accumulatedDistance = 0;
    }
  }
  
  // 确保包含终点
  if (result[result.length - 1] !== coordinates[coordinates.length - 1]) {
    result.push(coordinates[coordinates.length - 1]);
  }
  
  return result;
};

// 在两点之间插值生成新的点
export const interpolatePoints = (coordinates, maxPoints) => {
  if (coordinates.length <= maxPoints) return coordinates;
  
  const result = [];
  const step = coordinates.length / maxPoints;
  
  for (let i = 0; i < maxPoints - 1; i++) {
    const index = Math.floor(i * step);
    result.push(coordinates[index]);
  }
  
  // 确保包含终点
  result.push(coordinates[coordinates.length - 1]);
  
  return result;
};

// 使用 Mapbox Tilequery API 获取海拔数据
export const getElevationUsingTilequery = async (coordinates, dispatch, setLoadingElevation, setElevationStatus) => {
  try {
    // 设置加载状态
    setLoadingElevation(true);
    setElevationStatus('正在获取海拔数据...');
    
    console.log("开始获取海拔数据", coordinates.length);
    
    // 批量处理 - 每批15个坐标
    const batchSize = 15;
    const batches = [];
    
    for (let i = 0; i < coordinates.length; i += batchSize) {
      batches.push(coordinates.slice(i, i + batchSize));
    }
    
    const results = [];
    
    // 处理每个批次
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const progress = (batchIndex / batches.length) * 100;
      // 更新进度
      setElevationStatus(`正在获取海拔数据... ${Math.round(progress)}%`);
      
      console.log(`处理批次 ${batchIndex + 1}/${batches.length}, ${batch.length} 个坐标`);
      
      // 并行处理每个批次中的坐标
      const batchPromises = batch.map(async (coord, coordIndex) => {
        const { lng, lat } = coord;
        
        // 使用 mapbox-terrain-v2 数据源，但增加半径和结果限制以获取更多数据点
        const url = `https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery/${lng},${lat}.json?radius=30&limit=50&access_token=${mapboxgl.accessToken}`;
        
        try {
          const response = await fetch(url);
          
          if (!response.ok) {
            console.error(`获取海拔数据失败: ${response.status} ${response.statusText}`);
            return null;
          }
          
          const data = await response.json();
          
          if (!data.features || data.features.length === 0) {
            console.warn(`坐标 ${lng},${lat} 没有返回海拔数据`);
            return null;
          }
          
          // 查找具有高度属性的特征
          let elevation = null;
          
          // 优先查找最近的等高线点
          const contourFeatures = data.features.filter(f => 
            f.properties && f.properties.ele !== undefined && 
            f.properties.layer === 'contour'
          );
          
          if (contourFeatures.length > 0) {
            // 按距离排序
            contourFeatures.sort((a, b) => a.properties.tilequery.distance - b.properties.tilequery.distance);
            elevation = contourFeatures[0].properties.ele;
          } else {
            // 无等高线点时，查找任何具有海拔数据的点
            const elevationFeatures = data.features.filter(f => 
              f.properties && f.properties.ele !== undefined
            );
            
            if (elevationFeatures.length > 0) {
              elevationFeatures.sort((a, b) => a.properties.tilequery.distance - b.properties.tilequery.distance);
              elevation = elevationFeatures[0].properties.ele;
            }
          }
          
          if (elevation !== null) {
            // 保留一位小数的精度
            elevation = Math.round(elevation * 10) / 10;
            console.log(`坐标 ${lng.toFixed(5)},${lat.toFixed(5)} 的海拔: ${elevation} 米`);
            
            // 计算从起点的距离
            const distance = coordIndex === 0 ? 0 : 
              results.length > 0 ? 
                calculateTotalDistance([
                  ...results.filter(r => r !== null).map(r => [r.lng, r.lat]), 
                  [lng, lat]
                ]) * 1000 : 0; // 转换为米
            
            return {
              lng,
              lat,
              elevation,  // 为了与后续代码兼容，将ele作为elevation返回
              ele: elevation,
              distance
            };
          } else {
            console.warn(`坐标 ${lng},${lat} 没有找到海拔数据`);
            return null;
          }
        } catch (error) {
          console.error(`获取海拔数据时出错: ${error.message}`, error);
          return null;
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      const validResults = batchResults.filter(result => result !== null);
      results.push(...validResults);
      
      // 在批次之间添加小延迟，避免触发API速率限制
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // 完成加载
    setElevationStatus('海拔数据获取完成');
    
    console.log(`海拔数据获取完成，成功获取 ${results.length}/${coordinates.length} 个点的数据`);
    
    return results;
  } catch (error) {
    console.error("获取海拔数据时出错", error);
    // 设置错误状态
    setElevationStatus('获取海拔数据失败');
    setLoadingElevation(false);
    throw error;
  }
};

// 平滑海拔数据，减少阶梯状效果
export const smoothElevationData = (elevationData, smoothingFactor = 0.3) => {
  if (elevationData.length < 3) return elevationData;
  
  console.log('开始平滑处理海拔数据...');
  
  // 创建副本，避免修改原始数据
  const smoothed = JSON.parse(JSON.stringify(elevationData));
  
  // 第一次平滑：移动平均
  for (let i = 1; i < smoothed.length - 1; i++) {
    const prev = smoothed[i-1].elevation;
    const curr = smoothed[i].elevation;
    const next = smoothed[i+1].elevation;
    
    // 三点移动平均
    smoothed[i].elevation = prev * 0.25 + curr * 0.5 + next * 0.25;
  }
  
  // 第二次平滑：处理平台（连续相同高度）
  for (let i = 0; i < smoothed.length - 2; i++) {
    // 检测平台：连续三个或更多点高度几乎相同
    if (Math.abs(smoothed[i].elevation - smoothed[i+1].elevation) < 0.5 &&
        Math.abs(smoothed[i+1].elevation - smoothed[i+2].elevation) < 0.5) {
      
      // 寻找平台结束位置
      let j = i + 3;
      while (j < smoothed.length && 
             Math.abs(smoothed[j].elevation - smoothed[i].elevation) < 0.5) {
        j++;
      }
      
      // 如果找到了平台结束位置，并且与平台开始位置不同
      if (j < smoothed.length && Math.abs(smoothed[j].elevation - smoothed[i].elevation) >= 0.5) {
        // 对平台内的点进行线性插值
        const startElev = smoothed[i].elevation;
        const endElev = smoothed[j].elevation;
        const platformLength = j - i;
        
        for (let k = i + 1; k < j; k++) {
          const ratio = (k - i) / platformLength;
          // 线性插值
          smoothed[k].elevation = startElev + (endElev - startElev) * ratio;
        }
        
        // 跳到平台结束位置
        i = j - 1;
      }
    }
  }
  
  console.log('海拔数据平滑处理完成');
  return smoothed;
};

// 获取沿路线的海拔数据
export const fetchElevationData = async (coordinates, mapRef, setLoadingElevation, setElevationStatus, setElevationData) => {
  console.log('开始fetchElevationData, 坐标数量:', coordinates.length);
  
  if (!coordinates || coordinates.length < 2) {
    console.warn('路线坐标点不足，无法获取海拔数据');
    setElevationStatus(null);
    setElevationData([]);
    return;
  }
  
  // 设置加载状态
  setLoadingElevation(true);
  setElevationStatus('正在获取海拔数据...');
  
  try {
    console.log('开始获取海拔数据，坐标点数:', coordinates.length);
    
    // 对较长的路线进行采样，以减少需要处理的点数
    let sampledCoordinates = coordinates;
    if (coordinates.length > 100) {
      console.log('路线坐标点过多，进行采样处理');
      const sampleInterval = Math.ceil(coordinates.length / 100);
      sampledCoordinates = coordinates.filter((_, index) => index % sampleInterval === 0);
      // 确保包含起点和终点
      if (sampledCoordinates[sampledCoordinates.length - 1] !== coordinates[coordinates.length - 1]) {
        sampledCoordinates.push(coordinates[coordinates.length - 1]);
      }
      console.log(`采样后的坐标点数: ${sampledCoordinates.length} (原始点数: ${coordinates.length})`);
    }
    
    let elevationResults = [];
    let methodUsed = ''; // 记录使用的获取方法
    
    // 1. 首先尝试使用Google Maps JavaScript API（如果可用）
    if (window.google && window.google.maps) {
      try {
        console.log('尝试方法1: 使用Google Maps JavaScript API获取海拔数据');
        elevationResults = await getElevationUsingGoogleMapsJS(
          sampledCoordinates.map(coord => ({ lng: coord[0], lat: coord[1] })),
          setLoadingElevation,
          setElevationStatus
        );
        
        if (elevationResults && elevationResults.length >= sampledCoordinates.length * 0.5) {
          console.log('Google Maps JavaScript API获取数据成功');
          methodUsed = 'Google Maps JavaScript API';
        } else {
          console.warn('Google Maps JavaScript API返回的数据不足');
          elevationResults = []; // 重置结果，尝试下一种方法
        }
      } catch (error) {
        console.error('Google Maps JavaScript API获取数据失败:', error);
        elevationResults = []; // 重置结果，尝试下一种方法
      }
    } else {
      console.warn('Google Maps JavaScript API未加载，跳过此方法');
    }
    
    // 2. 如果JavaScript API不可用或失败，尝试使用Google Elevation REST API（通过代理）
    if (elevationResults.length < sampledCoordinates.length * 0.5) {
      try {
        console.log('尝试方法2: 使用Google Elevation REST API获取海拔数据');
        elevationResults = await getElevationUsingGoogleAPI(
          sampledCoordinates.map(coord => ({ lng: coord[0], lat: coord[1] })),
          null,
          setLoadingElevation,
          setElevationStatus
        );
        
        if (elevationResults && elevationResults.length >= sampledCoordinates.length * 0.5) {
          console.log('Google Elevation REST API获取数据成功');
          methodUsed = 'Google Elevation REST API';
        } else {
          console.warn('Google Elevation REST API返回的数据不足或为空');
          elevationResults = []; // 重置结果，尝试下一种方法
        }
      } catch (error) {
        console.error('Google Elevation REST API获取数据失败:', error);
        elevationResults = []; // 重置结果，尝试下一种方法
      }
    }
    
    // 3. 如果前两种方法都失败，尝试使用Mapbox地形查询API
    if (elevationResults.length < sampledCoordinates.length * 0.5) {
      console.log('尝试方法3: 使用地形查询API获取海拔数据');
      // ... 这里是现有的Mapbox地形查询代码 ...
      if (mapRef && mapRef.current) {
        console.log('开始使用地形查询API获取海拔数据');
        
        // 确保地形数据已加载
        if (!mapRef.current.getSource('mapbox-dem')) {
          console.log('地形数据源未加载，尝试添加高精度地形数据');
          try {
            mapRef.current.addSource('mapbox-dem', {
              'type': 'raster-dem',
              'url': 'mapbox://mapbox.terrain-rgb',  // 使用更高精度的terrain-rgb数据源
              'tileSize': 256,  // 减小瓦片尺寸以提高精度
              'maxzoom': 15     // 增加最大缩放级别以获取更精细的数据
            });
            mapRef.current.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 });  // 略微增强地形效果
            console.log('高精度地形数据加载成功');
            
            // 等待地形数据完全加载
            await new Promise(resolve => setTimeout(resolve, 2000));  // 增加等待时间确保数据加载完成
          } catch (error) {
            console.error('添加高精度地形数据源失败:', error);
          }
        }
        
        // 检查地图是否支持terrain查询
        if (typeof mapRef.current.queryTerrainElevation === 'function') {
          console.log('地图支持地形查询功能，开始查询高精度海拔数据...');
          
          // 对路线点进行更密集采样
          let denseCoordinates = sampledCoordinates;
          if (sampledCoordinates.length < 300) {
            console.log('增加采样密度以获取更精确的海拔曲线');
            denseCoordinates = sampleCoordinatesByDistance(coordinates, 50);  // 每50米一个点
            console.log(`增密后的坐标点数: ${denseCoordinates.length}`);
          }
          
          // 逐点查询海拔
          for (let i = 0; i < denseCoordinates.length; i++) {
            const [lng, lat] = denseCoordinates[i];
            setElevationStatus(`正在获取海拔数据... ${Math.round((i / denseCoordinates.length) * 100)}%`);
            
            try {
              const elevation = mapRef.current.queryTerrainElevation([lng, lat]);
              
              if (elevation !== null) {
                // 计算从起点的距离
                const distance = i === 0 ? 0 : 
                  calculateTotalDistance(denseCoordinates.slice(0, i + 1)) * 1000; // 转换为米
                
                elevationResults.push({
                  distance,
                  elevation: Math.round(elevation * 10) / 10,  // 保留一位小数
                  lng,
                  lat
                });
              } else {
                console.warn(`点[${i}] [${lng}, ${lat}] 未返回海拔数据`);
              }
            } catch (err) {
              console.error(`查询点[${i}] [${lng}, ${lat}]海拔时出错:`, err);
            }
          }
          
          console.log(`地形查询完成，获取到${elevationResults.length}个点的海拔数据`);
          
          if (elevationResults.length >= denseCoordinates.length * 0.7) {
            console.log('Mapbox地形查询获取数据成功');
            methodUsed = 'Mapbox地形查询';
          }
          
          // 如果获取了足够的数据点，进行平滑处理
          if (elevationResults.length > 3 && elevationResults.length >= denseCoordinates.length * 0.7) {
            console.log('对海拔数据进行平滑处理，减少阶梯状效果');
            elevationResults = smoothElevationData(elevationResults);
          }
        } else {
          console.warn('地图不支持queryTerrainElevation方法，将使用Tilequery API');
        }
      }
    }
    
    // 4. 如果前三种方法都失败，最后尝试使用Mapbox Tilequery API
    if (elevationResults.length < sampledCoordinates.length * 0.5) {
      console.log('尝试方法4: 使用Mapbox Tilequery API获取海拔数据');
      try {
        elevationResults = await getElevationUsingTilequery(
          sampledCoordinates.map(coord => ({ lng: coord[0], lat: coord[1] })), // 转换坐标格式
          null, // dispatch 参数不再使用，传null
          setLoadingElevation,
          setElevationStatus
        );
        
        if (elevationResults && elevationResults.length >= sampledCoordinates.length * 0.5) {
          console.log('Mapbox Tilequery API获取数据成功');
          methodUsed = 'Mapbox Tilequery API';
        } else {
          console.warn('Mapbox Tilequery API返回的数据不足');
        }
      } catch (error) {
        console.error('Mapbox Tilequery API获取数据失败:', error);
      }
    }
    
    // 如果所有方法都失败，返回空结果
    if (elevationResults.length < sampledCoordinates.length * 0.3) {
      console.error('所有获取海拔数据的方法都失败了');
      setElevationStatus('获取海拔数据失败，请稍后重试');
      setElevationData([]);
      setLoadingElevation(false);
      return;
    }
    
    console.log(`成功使用 ${methodUsed} 获取海拔数据，数据点数: ${elevationResults.length}`);
    
    // 处理海拔结果，计算累计爬升和坡度
    let cumulativeElevationGain = 0;
    let previousElevation = elevationResults.length > 0 ? elevationResults[0].elevation : 0;
    let previousGradient = 0;
    
    const processedResults = elevationResults.map((result, index) => {
      // 计算累计爬升
      if (index > 0 && result.elevation > previousElevation) {
        cumulativeElevationGain += (result.elevation - previousElevation);
      }
      
      // 计算坡度
      let gradient = 0;
      if (index > 0) {
        const prevResult = elevationResults[index - 1];
        const segmentDistance = result.distance - prevResult.distance;
        
        if (segmentDistance > 0) {
          gradient = ((result.elevation - prevResult.elevation) / segmentDistance) * 100;
          
          // 应用平滑处理，避免极端值
          gradient = gradient * 0.7 + previousGradient * 0.3;
          previousGradient = gradient;
        }
      }
      
      previousElevation = result.elevation;
      
      // 返回处理后的结果
      return {
        ...result,
        cumulativeElevationGain: Math.round(cumulativeElevationGain),
        gradient: parseFloat(gradient.toFixed(1))
      };
    });
    
    console.log('海拔数据处理完成，数据点数量:', processedResults.length);
    setElevationData(processedResults);
    setElevationStatus(null);
  } catch (error) {
    console.error('获取海拔数据失败:', error);
    setElevationStatus('获取海拔数据失败，请稍后重试');
    setElevationData([]);
  } finally {
    setLoadingElevation(false);
  }
};

// 使用Google Elevation API获取海拔数据
export const getElevationUsingGoogleAPI = async (coordinates, dispatch, setLoadingElevation, setElevationStatus) => {
  try {
    // 设置加载状态
    setLoadingElevation(true);
    setElevationStatus('正在获取海拔数据...');
    
    console.log("开始使用Google Elevation API获取海拔数据", coordinates.length);
    
    const API_KEY = 'AIzaSyBQVcI4AsZdhFPRD80yRe4Gu2P2_Oh5wVg';
    // 是否使用代理服务器
    const USE_PROXY = true; // 启用代理服务器，解决CORS问题
    // 您需要使用自己的代理服务器或配置开发服务器代理
    const PROXY_URL = 'https://corsproxy.io/?'; // 这是一个示例公共代理，实际使用请替换为您自己的代理
    
    // 每批次最多处理500个点（Google API限制）
    const batchSize = 500;
    const batches = [];
    
    // 对于大型路线，先进行预处理以减少API调用次数
    let processedCoordinates = coordinates;
    if (coordinates.length > 1000) {
      console.log("路线点数过多，将进行采样处理");
      // 对于超长路线，采样处理，最多处理1000个点
      const samplingInterval = Math.ceil(coordinates.length / 1000);
      processedCoordinates = coordinates.filter((_, index) => index % samplingInterval === 0 || index === coordinates.length - 1);
      console.log(`采样后的坐标点数: ${processedCoordinates.length} (原始点数: ${coordinates.length})`);
    }
    
    for (let i = 0; i < processedCoordinates.length; i += batchSize) {
      batches.push(processedCoordinates.slice(i, i + batchSize));
    }
    
    console.log(`将分${batches.length}批处理，每批最多${batchSize}个点`);
    
    const results = [];
    
    // 处理每个批次
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const progress = (batchIndex / batches.length) * 100;
      // 更新进度
      setElevationStatus(`正在获取海拔数据... ${Math.round(progress)}%`);
      
      console.log(`处理批次 ${batchIndex + 1}/${batches.length}, ${batch.length} 个坐标`);
      
      // 构建请求参数 - 确保使用正确的格式
      // 使用locations参数，这是API的必要参数，而且需要正确编码
      // 格式应为 lat,lng|lat,lng 的形式
      const locations = batch.map(coord => {
        // 确保坐标是数字并且有效
        const lat = parseFloat(coord.lat);
        const lng = parseFloat(coord.lng);
        if (isNaN(lat) || isNaN(lng)) {
          console.warn(`忽略无效坐标: lat=${coord.lat}, lng=${coord.lng}`);
          return null;
        }
        return `${lat},${lng}`;
      }).filter(coord => coord !== null).join('|');
      
      // 使用encodeURIComponent确保URL安全
      const encodedLocations = encodeURIComponent(locations);
      
      // 构建URL - 使用开发环境代理或公共代理
      let requestUrl;
      
      if (process.env.NODE_ENV === 'development') {
        // 使用本地开发服务器代理（前提是已配置setupProxy.js）
        requestUrl = `/google-elevation/json?locations=${encodedLocations}&key=${API_KEY}`;
      } else if (USE_PROXY) {
        // 使用公共代理服务器（生产环境或本地代理未配置）
        requestUrl = `${PROXY_URL}https://maps.googleapis.com/maps/api/elevation/json?locations=${encodedLocations}&key=${API_KEY}`;
      } else {
        // 直接请求（可能会有CORS问题）
        requestUrl = `https://maps.googleapis.com/maps/api/elevation/json?locations=${encodedLocations}&key=${API_KEY}`;
      }
      
      // 打印请求URL的部分信息（出于安全考虑不打印完整URL）
      console.log(`发送请求到Google Elevation API, URL长度: ${requestUrl.length}, 请求坐标数量: ${batch.length}, 使用${process.env.NODE_ENV === 'development' ? '本地代理' : USE_PROXY ? '公共代理' : '直接请求'}`);
      
      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          mode: 'cors', // 确保启用跨域模式
        });
        
        if (!response.ok) {
          console.error(`获取海拔数据失败: ${response.status} ${response.statusText}`);
          // 尝试读取错误响应内容
          try {
            const errorText = await response.text();
            console.error('错误响应内容:', errorText);
          } catch (e) {
            console.error('无法读取错误响应内容');
          }
          continue;
        }
        
        // 获取原始响应文本并打印
        const responseText = await response.text();
        console.log(`收到Google Elevation API响应, 长度: ${responseText.length}`);
        
        // 检查响应是否为空
        if (!responseText || responseText.trim() === '') {
          console.error('Google Elevation API返回了空响应');
          continue;
        }
        
        // 尝试将响应解析为JSON
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error(`解析Google Elevation API响应失败: ${e.message}`);
          console.log('响应内容:', responseText.substring(0, 200) + (responseText.length > 200 ? '...' : ''));
          continue;
        }
        
        if (data.status !== 'OK') {
          console.error(`Google Elevation API返回错误: ${data.status}`, data.error_message ? `错误信息: ${data.error_message}` : '');
          continue;
        }
        
        // 检查返回的结果数量
        if (!data.results || data.results.length !== batch.length) {
          console.warn(`返回的结果数量(${data.results ? data.results.length : 0})与请求的坐标数量(${batch.length})不匹配`);
        }
        
        // 处理结果
        data.results.forEach((result, i) => {
          const coord = batch[i];
          const { elevation } = result;
          
          // 保留一位小数的精度
          const roundedElevation = Math.round(elevation * 10) / 10;
          
          // 计算从起点的距离
          const distance = results.length === 0 ? 0 : 
            calculateTotalDistance([
              ...results.map(r => [r.lng, r.lat]), 
              [coord.lng, coord.lat]
            ]) * 1000; // 转换为米
          
          console.log(`坐标 ${coord.lng.toFixed(5)},${coord.lat.toFixed(5)} 的海拔: ${roundedElevation} 米`);
          
          results.push({
            lng: coord.lng,
            lat: coord.lat,
            elevation: roundedElevation,
            ele: roundedElevation,
            distance
          });
        });
      } catch (error) {
        console.error(`获取海拔数据时出错: ${error.message}`, error);
        // 继续处理下一批次
      }
      
      // 在批次之间添加小延迟，避免触发API速率限制
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    // 完成加载
    setElevationStatus('海拔数据获取完成');
    
    console.log(`海拔数据获取完成，成功获取 ${results.length}/${processedCoordinates.length} 个点的数据`);
    
    // 对获取的海拔数据进行平滑处理
    if (results.length > 3) {
      console.log('对海拔数据进行平滑处理，减少异常值');
      // 创建副本，避免修改原始数据
      const processedResults = [...results];
      
      // 第一步：检测和处理离群值
      for (let i = 1; i < processedResults.length - 1; i++) {
        const prev = processedResults[i-1].elevation;
        const curr = processedResults[i].elevation;
        const next = processedResults[i+1].elevation;
        
        // 检测离群值 - 当前点与前后点差异过大
        const diffPrev = Math.abs(curr - prev);
        const diffNext = Math.abs(curr - next);
        
        if (diffPrev > 100 && diffNext > 100) {
          // 认为是离群值，使用前后点的平均值替代
          console.log(`检测到离群值: ${curr}米 在索引 ${i}, 与前值差: ${diffPrev}, 与后值差: ${diffNext}`);
          processedResults[i].elevation = (prev + next) / 2;
          processedResults[i].ele = (prev + next) / 2;
        }
      }
      
      // 第二步：应用移动平均平滑
      for (let i = 1; i < processedResults.length - 1; i++) {
        const prev = processedResults[i-1].elevation;
        const curr = processedResults[i].elevation;
        const next = processedResults[i+1].elevation;
        
        // 加权移动平均平滑
        const smoothed = prev * 0.25 + curr * 0.5 + next * 0.25;
        processedResults[i].elevation = Math.round(smoothed * 10) / 10;
        processedResults[i].ele = Math.round(smoothed * 10) / 10;
      }
      
      return processedResults;
    }
    
    return results;
  } catch (error) {
    console.error("获取海拔数据时出错", error);
    // 设置错误状态
    setElevationStatus('获取海拔数据失败');
    setLoadingElevation(false);
    throw error;
  }
};

// 使用Google Maps JavaScript API获取海拔数据
export const getElevationUsingGoogleMapsJS = async (coordinates, setLoadingElevation, setElevationStatus) => {
  try {
    // 设置加载状态
    setLoadingElevation(true);
    setElevationStatus('正在获取海拔数据...');
    
    console.log("开始使用Google Maps JavaScript API获取海拔数据", coordinates.length);
    
    // 检查是否已经加载Google Maps JavaScript API
    if (!window.google || !window.google.maps) {
      console.error('Google Maps JavaScript API未加载');
      throw new Error('Google Maps JavaScript API未加载');
    }
    
    // 创建Elevation服务实例
    const elevator = new window.google.maps.ElevationService();
    
    // 每批次最多处理500个点
    const batchSize = 500;
    const batches = [];
    
    // 对于大型路线，先进行预处理以减少API调用次数
    let processedCoordinates = coordinates;
    if (coordinates.length > 1000) {
      console.log("路线点数过多，将进行采样处理");
      // 对于超长路线，采样处理，最多处理1000个点
      const samplingInterval = Math.ceil(coordinates.length / 1000);
      processedCoordinates = coordinates.filter((_, index) => index % samplingInterval === 0 || index === coordinates.length - 1);
      console.log(`采样后的坐标点数: ${processedCoordinates.length} (原始点数: ${coordinates.length})`);
    }
    
    for (let i = 0; i < processedCoordinates.length; i += batchSize) {
      batches.push(processedCoordinates.slice(i, i + batchSize));
    }
    
    console.log(`将分${batches.length}批处理，每批最多${batchSize}个点`);
    
    const results = [];
    
    // 处理每个批次
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const progress = (batchIndex / batches.length) * 100;
      // 更新进度
      setElevationStatus(`正在获取海拔数据... ${Math.round(progress)}%`);
      
      console.log(`处理批次 ${batchIndex + 1}/${batches.length}, ${batch.length} 个坐标`);
      
      // 构建请求参数 - 转换为Google Maps LatLng对象
      const locations = batch.map(coord => ({
        lat: parseFloat(coord.lat),
        lng: parseFloat(coord.lng)
      }));
      
      try {
        // 使用Promise包装API调用
        const batchResults = await new Promise((resolve, reject) => {
          elevator.getElevationForLocations({
            locations: locations
          }, (elevationResults, status) => {
            if (status === 'OK' && elevationResults) {
              resolve(elevationResults);
            } else {
              reject(new Error(`获取海拔失败: ${status}`));
            }
          });
        });
        
        // 处理结果
        batchResults.forEach((result, i) => {
          const coord = batch[i];
          const { elevation } = result;
          
          // 保留一位小数的精度
          const roundedElevation = Math.round(elevation * 10) / 10;
          
          // 计算从起点的距离
          const distance = results.length === 0 ? 0 : 
            calculateTotalDistance([
              ...results.map(r => [r.lng, r.lat]), 
              [coord.lng, coord.lat]
            ]) * 1000; // 转换为米
          
          results.push({
            lng: coord.lng,
            lat: coord.lat,
            elevation: roundedElevation,
            ele: roundedElevation,
            distance
          });
        });
        
        // 在批次之间添加小延迟，避免触发API速率限制
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error(`批次 ${batchIndex + 1} 处理出错:`, error);
        // 继续处理下一批次
      }
    }
    
    // 完成加载
    setElevationStatus('海拔数据获取完成');
    
    console.log(`海拔数据获取完成，成功获取 ${results.length}/${processedCoordinates.length} 个点的数据`);
    
    return results;
  } catch (error) {
    console.error("获取海拔数据时出错", error);
    // 设置错误状态
    setElevationStatus('获取海拔数据失败');
    setLoadingElevation(false);
    throw error;
  }
}; 